import { z } from "zod";
import { payeeLabelSchema } from "./payee-label.js";

/* ===========================================================================
 * US-E18.F5-0198 (@UC9288–@UC9291) — CỌC (deposit) · PHÍ PHÂN BỔ (tip/VAT/service) · KHẤU HAO tài
 * sản chung. Phase Post-MVP — @gate money,ledger 🔴.
 *
 * 🔴 BA BẤT BIẾN (response TRẢ số để client TỰ KIỂM):
 *  (1) CỌC = LUÂN CHUYỂN, KHÔNG phải chi phí ⇒ `fundExpenseMinor` (Σ chi phí sổ) BẤT BIẾN khi
 *      đặt cọc và khi hoàn ĐỦ. Mất cọc ⇒ chi phí tăng ĐÚNG `forfeitedMinor` (không phải cả cọc).
 *  (2) PHÍ: Σ (suất gốc + tip + VAT) = `grandTotalMinor` — không lệch 1 đồng; `residualHolderId`
 *      = người gánh ĐỒNG LẺ, CÓ TÊN (P1.3 — không làm tròn mù).
 *  (3) KHẤU HAO: mua tài sản ⇒ 0 chi phí (tài sản); mỗi lần dùng ⇒ 1 bút toán;
 *      `accumulatedDepreciationMinor` ≤ `totalCostMinor` LUÔN (hết kỳ ⇒ 409).
 *
 * B1 tiền = STRING minor (^\d+$) — KHÔNG float · C5 `.strict()` chặn over-post.
 * ========================================================================= */

const POSITIVE_INTEGER_STRING_PATTERN = /^\d+$/;
const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;
const PG_BIGINT_MAX = 9223372036854775807n;
const AMOUNT_MAX_DIGITS = 19;
const TEXT_MAX = 500;
const IDEMPOTENCY_KEY_MAX = 200;

export const MONEY_INVALID_MESSAGE = "Số tiền phải là số nguyên (đơn vị nhỏ nhất), không dấu phẩy";

/** Tiền > 0 (minor, string). */
const positiveMoneyString = z
  .string()
  .trim()
  .min(1, MONEY_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, MONEY_INVALID_MESSAGE)
  .regex(POSITIVE_INTEGER_STRING_PATTERN, MONEY_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n > 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, MONEY_INVALID_MESSAGE);

/** Tiền ≥ 0 (minor, string) — dùng cho phần MẤT cọc (0 = hoàn đủ) và phí (0 = không tip). */
const nonNegativeMoneyString = z
  .string()
  .trim()
  .min(1, MONEY_INVALID_MESSAGE)
  .max(AMOUNT_MAX_DIGITS, MONEY_INVALID_MESSAGE)
  .regex(POSITIVE_INTEGER_STRING_PATTERN, MONEY_INVALID_MESSAGE)
  .refine((v) => {
    try {
      const n = BigInt(v);
      return n >= 0n && n <= PG_BIGINT_MAX;
    } catch {
      return false;
    }
  }, MONEY_INVALID_MESSAGE);

/* ═════════════════════════════ CỌC (deposit) ═════════════════════════════ */

/** Trạng thái cọc — mirror DB CHECK `deposits_status_check` + domain DEPOSIT_STATUSES. */
export const DEPOSIT_STATUS = {
  HELD: "held",
  REFUNDED: "refunded",
  PARTIAL_FORFEIT: "partial_forfeit",
  FORFEITED: "forfeited",
} as const;

export type DepositStatusValue = (typeof DEPOSIT_STATUS)[keyof typeof DEPOSIT_STATUS];

export const depositStatusSchema = z.enum([
  DEPOSIT_STATUS.HELD,
  DEPOSIT_STATUS.REFUNDED,
  DEPOSIT_STATUS.PARTIAL_FORFEIT,
  DEPOSIT_STATUS.FORFEITED,
]);

/** Nguồn tiền cọc: quỹ chi (`fund`) hay member ứng trước (`member`). */
export const DEPOSIT_SOURCE = { FUND: "fund", MEMBER: "member" } as const;
export type DepositSourceValue = (typeof DEPOSIT_SOURCE)[keyof typeof DEPOSIT_SOURCE];
export const depositSourceSchema = z.enum([DEPOSIT_SOURCE.FUND, DEPOSIT_SOURCE.MEMBER]);

/** 409 — cọc đã tất toán (hoàn/mất) ⇒ không tất toán lần nữa (chống double-settle). */
export const DEPOSIT_NOT_HELD_CODE = "DEPOSIT_NOT_HELD" as const;
/** 422 — phần mất > tiền cọc (không thể mất nhiều hơn đã đặt). */
export const DEPOSIT_FORFEIT_EXCEEDS_CODE = "DEPOSIT_FORFEIT_EXCEEDS" as const;
/** 422 — member không thuộc nhóm sở hữu sổ (chống poison). */
export const DEPOSIT_MEMBER_INVALID_CODE = "DEPOSIT_MEMBER_INVALID" as const;

/** POST /v1/funds/:id/deposits — đặt cọc (tiền LUÂN CHUYỂN, KHÔNG ghi chi phí). */
export const placeDepositRequestSchema = z
  .object({
    amountMinor: positiveMoneyString,
    // NAME-SANITIZE-PAYEE-FIELDS (@UC0055): đối tác đặt cọc (nhà hàng, chủ nhà…) — gộp khoảng trắng + chặn ký tự không in được.
    counterparty: payeeLabelSchema((s) => s.min(1).max(TEXT_MAX)),
    source: depositSourceSchema.default(DEPOSIT_SOURCE.FUND),
    /** BẮT BUỘC khi source='member' (người ứng tiền — quỹ ghi nợ người này). */
    paidByMemberId: z.string().uuid().optional(),
    note: z.string().trim().max(TEXT_MAX).optional(),
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
  })
  .strict();

export type PlaceDepositRequest = z.infer<typeof placeDepositRequestSchema>;

/** POST /v1/funds/:id/deposits/:depositId/settle — hoàn / mất (một phần hoặc toàn bộ). */
export const settleDepositRequestSchema = z
  .object({
    /** 0 ⇒ HOÀN ĐỦ (0 chi phí). > 0 ⇒ phần MẤT thành chi phí chung (chia cho nhóm). */
    forfeitedMinor: nonNegativeMoneyString,
    note: z.string().trim().max(TEXT_MAX).optional(),
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
  })
  .strict();

export type SettleDepositRequest = z.infer<typeof settleDepositRequestSchema>;

/** Một suất chia phần MẤT cọc cho member (chi phí chung) — Σ = forfeitedMinor. */
export const forfeitShareSchema = z
  .object({
    memberId: z.string().uuid(),
    shareMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type ForfeitShare = z.infer<typeof forfeitShareSchema>;

export const depositResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    source: depositSourceSchema,
    paidByMemberId: z.string().uuid().nullable(),
    counterparty: z.string(),
    amountMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    status: depositStatusSchema,
    refundedMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    forfeitedMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    placeLedgerTxId: z.string().uuid().nullable(),
    settleLedgerTxId: z.string().uuid().nullable(),
    /** 🔴 Σ CASH của sổ SAU thao tác — client tự kiểm luân chuyển tiền. */
    fundCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** 🔴 Σ CHI PHÍ của sổ SAU thao tác — đặt cọc/hoàn đủ ⇒ BẤT BIẾN (cọc ≠ chi phí). */
    fundExpenseMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Chia phần MẤT cho nhóm (rỗng khi hoàn đủ) — Σ = forfeitedMinor, đồng lẻ CÓ TÊN. */
    forfeitShares: z.array(forfeitShareSchema),
    /** 🔴 P1.3 — người gánh đồng lẻ khi chia phần mất (null khi không mất gì). */
    forfeitResidualHolderId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    settledAt: z.string().datetime().nullable(),
  })
  .strict();

export type DepositResponse = z.infer<typeof depositResponseSchema>;

export const depositListResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    currency: z.string().length(3),
    deposits: z.array(depositResponseSchema),
    /** Σ cọc ĐANG TREO (held) — tiền của nhóm đang ở bên thứ ba, KHÔNG phải chi phí. */
    heldTotalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type DepositListResponse = z.infer<typeof depositListResponseSchema>;

/* ═══════════════════ PHÍ PHÂN BỔ (tip / VAT / service) ═══════════════════ */

export const FEE_KIND = { TIP: "tip", VAT: "vat", SERVICE: "service" } as const;
export type FeeKindValue = (typeof FEE_KIND)[keyof typeof FEE_KIND];
export const feeKindSchema = z.enum([FEE_KIND.TIP, FEE_KIND.VAT, FEE_KIND.SERVICE]);

/** 422 — phân bổ lệch Σ (chốt chặn P1.3 — không bao giờ được xảy ra, fail-loud nếu có). */
export const FEE_ALLOCATION_MISMATCH_CODE = "FEE_ALLOCATION_MISMATCH" as const;
/**
 * 422 — `expenseId` không phải khoản chi của CHÍNH quỹ này (không tồn tại · quỹ khác cùng nhóm · nhóm khác).
 * Cố ý MỘT mã cho cả ba (chống dò: khoản chi của quỹ khác trả y như không tồn tại, không lộ id/tên quỹ).
 */
export const FEE_ALLOCATION_EXPENSE_NOT_FOUND_CODE = "FEE_ALLOCATION_EXPENSE_NOT_FOUND" as const;
/** 422 — khoản chi của quỹ này nhưng KHÔNG ở trạng thái đã ghi sổ (`posted`); `params.status` = trạng thái hiện tại. */
export const FEE_ALLOCATION_EXPENSE_NOT_POSTED_CODE = "FEE_ALLOCATION_EXPENSE_NOT_POSTED" as const;
/**
 * 422 — `postExpense: true` kèm `expenseId`: khoản chi đã có đường ghi sổ RIÊNG của nó ⇒ quỹ trả hoá đơn lần hai
 * (bút toán EXPENSE thứ hai mang cùng `expenseId`). Gắn phân bổ phí vào khoản chi = CHỈ dấu vết (`postExpense: false`).
 */
export const FEE_ALLOCATION_EXPENSE_DOUBLE_POST_CODE = "FEE_ALLOCATION_EXPENSE_DOUBLE_POST" as const;

/** Suất gốc 1 người trên hoá đơn (trước phí). */
export const baseShareInputSchema = z
  .object({
    memberId: z.string().uuid(),
    /** Suất gốc (minor) — 0 = miễn (không gánh phí, vẫn hiện trong kết quả). */
    baseMinor: nonNegativeMoneyString,
  })
  .strict();

export const feeInputSchema = z
  .object({
    kind: feeKindSchema,
    amountMinor: nonNegativeMoneyString,
  })
  .strict();

/**
 * POST /v1/funds/:id/fee-allocations — phân bổ tip/VAT/phí vào các phần chia.
 * `postExpense` = quỹ TRẢ luôn tổng hoá đơn (base + phí) ⇒ post 1 bút toán EXPENSE (CASH −tổng).
 */
export const allocateFeesRequestSchema = z
  .object({
    baseShares: z.array(baseShareInputSchema).min(1),
    fees: z.array(feeInputSchema).max(3).default([]),
    /** Người gánh ĐỒNG LẺ (P1.3) — vắng ⇒ server chọn xác định (member đầu theo sort id). */
    residualHolderId: z.string().uuid().optional(),
    /**
     * Khoản chi ĐÃ GHI SỔ (`posted`) của CHÍNH quỹ này mà hoá đơn thuộc về — chỉ là DẤU VẾT, đi với `postExpense: false`
     * (FEE-ALLOC-EXPENSEID: sai quỹ/không tồn tại ⇒ 422 FEE_ALLOCATION_EXPENSE_NOT_FOUND · chưa ghi sổ ⇒ 422 FEE_ALLOCATION_EXPENSE_NOT_POSTED
     * · kèm `postExpense: true` ⇒ 422 FEE_ALLOCATION_EXPENSE_DOUBLE_POST).
     */
    expenseId: z.string().uuid().optional(),
    postExpense: z.boolean().default(false),
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
  })
  .strict();

export type AllocateFeesRequest = z.infer<typeof allocateFeesRequestSchema>;

export const participantFeeShareSchema = z
  .object({
    memberId: z.string().uuid(),
    baseMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    feeByKind: z.record(feeKindSchema, z.string().regex(MONEY_INTEGER_STRING_PATTERN)),
    /** base + Σ phí gánh — Σ mọi người = grandTotalMinor CHÍNH XÁC. */
    totalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
  })
  .strict();

export type ParticipantFeeShare = z.infer<typeof participantFeeShareSchema>;

export const feeAllocationResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    expenseId: z.string().uuid().nullable(),
    currency: z.string().length(3),
    participants: z.array(participantFeeShareSchema),
    baseTotalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    feeTotalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** 🔴 Σ mọi suất = base + phí — không lệch 1 đồng (P1.3). */
    grandTotalMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** 🔴 Người gánh ĐỒNG LẺ — CÓ TÊN. */
    residualHolderId: z.string().uuid(),
    ledgerTxId: z.string().uuid().nullable(),
    fundCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    createdAt: z.string().datetime(),
  })
  .strict();

export type FeeAllocationResponse = z.infer<typeof feeAllocationResponseSchema>;

/* ═════════════ KHẤU HAO TÀI SẢN CHUNG (shared asset) ═════════════ */

/** 409 — tài sản đã khấu hao hết số kỳ (Σ khấu hao = nguyên giá) ⇒ KHÔNG phân bổ thêm. */
export const ASSET_FULLY_DEPRECIATED_CODE = "ASSET_FULLY_DEPRECIATED" as const;

const MAX_PERIODS = 1200;

/** POST /v1/funds/:id/shared-assets — mua tài sản chung (TÀI SẢN, KHÔNG phải chi phí ngay). */
export const createSharedAssetRequestSchema = z
  .object({
    name: z.string().trim().min(1).max(TEXT_MAX),
    totalCostMinor: positiveMoneyString,
    /** Số lần dùng/kỳ dự kiến (lều 2tr / 10 chuyến ⇒ 200k/chuyến). */
    periods: z.number().int().min(1).max(MAX_PERIODS),
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
  })
  .strict();

export type CreateSharedAssetRequest = z.infer<typeof createSharedAssetRequestSchema>;

/** POST /v1/funds/:id/shared-assets/:assetId/depreciate — phân bổ khấu hao 1 lần dùng. */
export const depreciateAssetRequestSchema = z
  .object({
    note: z.string().trim().max(TEXT_MAX).optional(),
    idempotencyKey: z.string().trim().min(1).max(IDEMPOTENCY_KEY_MAX),
  })
  .strict();

export type DepreciateAssetRequest = z.infer<typeof depreciateAssetRequestSchema>;

export const sharedAssetResponseSchema = z
  .object({
    id: z.string().uuid(),
    fundId: z.string().uuid(),
    name: z.string(),
    totalCostMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    periods: z.number().int(),
    usedPeriods: z.number().int(),
    /** 🔴 Σ khấu hao đã ghi — LUÔN ≤ totalCostMinor (không khấu hao quá nguyên giá). */
    accumulatedDepreciationMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Giá trị còn lại = totalCost − accumulated (≥ 0). */
    remainingValueMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    purchaseLedgerTxId: z.string().uuid().nullable(),
    /** 🔴 Σ CHI PHÍ của sổ SAU thao tác — mua tài sản ⇒ BẤT BIẾN (chưa phải chi phí). */
    fundExpenseMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    fundCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    createdAt: z.string().datetime(),
  })
  .strict();

export type SharedAssetResponse = z.infer<typeof sharedAssetResponseSchema>;

export const depreciationResponseSchema = z
  .object({
    id: z.string().uuid(),
    assetId: z.string().uuid(),
    fundId: z.string().uuid(),
    periodIndex: z.number().int(),
    amountMinor: z.string().regex(POSITIVE_INTEGER_STRING_PATTERN),
    currency: z.string().length(3),
    note: z.string().nullable(),
    ledgerTxId: z.string().uuid(),
    /** 🔴 Σ khấu hao lũy kế SAU lần này — ≤ nguyên giá LUÔN. */
    accumulatedDepreciationMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    remainingValueMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** Σ CHI PHÍ sổ SAU khi phân bổ (tăng ĐÚNG amountMinor). */
    fundExpenseMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    /** 🔴 Σ CASH BẤT BIẾN khi khấu hao (tiền đã ra lúc mua — không ra lần nữa). */
    fundCashMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
    createdAt: z.string().datetime(),
  })
  .strict();

export type DepreciationResponse = z.infer<typeof depreciationResponseSchema>;
